import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Input } from '@/design-system/ui/Input';
import { SimpleSelect } from '@/design-system/ui/Select';
import { cepDigits, formatCep, UFS } from '@escala/domain/members';
import type { Address } from '@escala/domain/types';
import { lookupCep } from '@/services/cep';

import FormField, { fieldErrorProps } from './FormField';

const UF_OPTIONS = [
  { value: 'none', label: 'Selecione', pin: true },
  ...UFS.map((uf) => ({ value: uf, label: uf })),
];

/**
 * Endereço com CEP: máscara enquanto se digita e, com os 8 dígitos, o preenchimento de
 * logradouro, bairro, cidade e UF pelo serviço público de CEP (o número e o complemento
 * ficam com a pessoa). Sem internet ou CEP desconhecido, ela digita.
 */
export default function AddressFields({
  idPrefix,
  address,
  disabled = false,
  optional = false,
  errors = {},
  onChange,
}: {
  idPrefix: string;
  address: Address;
  disabled?: boolean;
  /** Mostra "(opcional)" nos rótulos (formulário público). */
  optional?: boolean;
  errors?: { cep?: string; uf?: string };
  onChange: (address: Address) => void;
}) {
  const [looking, setLooking] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // O endereço mais recente, para a resposta do CEP não apagar o que foi digitado depois.
  const latest = useRef(address);

  useEffect(() => {
    latest.current = address;
  }, [address]);

  const id = (field: string) => `${idPrefix}-${field}`;
  const set = (patch: Partial<Address>) => {
    latest.current = { ...latest.current, ...patch };
    onChange(latest.current);
  };

  const onCep = (value: string) => {
    const digits = cepDigits(value);

    set({ cep: digits });
    setNotFound(false);

    if (digits.length !== 8) return;

    setLooking(true);
    void lookupCep(digits).then((found) => {
      setLooking(false);
      // A pessoa pode ter continuado digitando: só preenche se o CEP ainda é o mesmo.
      if (latest.current.cep !== digits) return;
      if (!found) {
        setNotFound(true);

        return;
      }

      set({
        street: found.street || latest.current.street,
        district: found.district || latest.current.district,
        city: found.city || latest.current.city,
        uf: found.uf || latest.current.uf,
      });
    });
  };

  const cepHint = () => {
    if (looking) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <LoaderCircle aria-hidden className="size-3 animate-spin" />
          Buscando o endereço…
        </span>
      );
    }

    if (notFound) return 'CEP não encontrado. Preencha o endereço.';

    return 'Ao digitar o CEP, preenchemos o endereço.';
  };

  return (
    <>
      <FormField id={id('cep')} label="CEP" optional={optional} error={errors.cep} hint={cepHint()}>
        <Input
          id={id('cep')}
          inputMode="numeric"
          autoComplete="postal-code"
          data-numeric
          maxLength={9}
          placeholder="00000-000"
          disabled={disabled}
          value={formatCep(address.cep)}
          onChange={(e) => onCep(e.target.value)}
          {...fieldErrorProps(id('cep'), errors.cep)}
        />
      </FormField>
      <FormField
        id={id('street')}
        label="Logradouro"
        optional={optional}
        className="col-span-full md:col-span-2"
      >
        <Input
          id={id('street')}
          autoComplete="address-line1"
          placeholder="Rua, avenida..."
          disabled={disabled}
          value={address.street}
          onChange={(e) => set({ street: e.target.value })}
        />
      </FormField>
      <FormField id={id('number')} label="Número" optional={optional}>
        <Input
          id={id('number')}
          inputMode="numeric"
          disabled={disabled}
          value={address.number}
          onChange={(e) => set({ number: e.target.value })}
        />
      </FormField>
      <FormField id={id('complement')} label="Complemento" optional={optional}>
        <Input
          id={id('complement')}
          autoComplete="address-line2"
          placeholder="Apto, bloco..."
          disabled={disabled}
          value={address.complement}
          onChange={(e) => set({ complement: e.target.value })}
        />
      </FormField>
      <FormField id={id('district')} label="Bairro" optional={optional}>
        <Input
          id={id('district')}
          disabled={disabled}
          value={address.district}
          onChange={(e) => set({ district: e.target.value })}
        />
      </FormField>
      <FormField id={id('city')} label="Cidade" optional={optional}>
        <Input
          id={id('city')}
          autoComplete="address-level2"
          disabled={disabled}
          value={address.city}
          onChange={(e) => set({ city: e.target.value })}
        />
      </FormField>
      <FormField id={id('uf')} label="UF" optional={optional} error={errors.uf}>
        <SimpleSelect
          id={id('uf')}
          aria-label="UF"
          disabled={disabled}
          value={address.uf || 'none'}
          onValueChange={(uf) => set({ uf: uf === 'none' ? '' : uf })}
          options={UF_OPTIONS}
        />
      </FormField>
    </>
  );
}
